<script setup>
import { computed, useAttrs } from 'vue'
import { useFormFieldContext } from '@/composables/useFormFieldContext'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue: { type: [Boolean, Array], default: false },
  /** Value when used in a checkbox group (array v-model). */
  value: { type: [String, Number, Boolean], default: true },
  /** Visible control label (required for a11y when BaseFormField has no label). */
  label: { type: String, default: '' },
  id: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  invalid: { type: Boolean, default: null },
  density: { type: String, default: '' },
  ariaLabel: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])
const attrs = useAttrs()
const { resolveId, resolveDescribedby, resolveInvalid, resolveDensity } = useFormFieldContext()

const resolvedId = computed(() => resolveId(props.id))
const resolvedInvalid = computed(() => resolveInvalid(props.invalid))
const resolvedDensity = computed(() => resolveDensity(props.density))
const describedby = computed(() => resolveDescribedby(attrs['aria-describedby']))

const isChecked = computed(() => {
  if (Array.isArray(props.modelValue)) {
    return props.modelValue.includes(props.value)
  }
  return !!props.modelValue
})

function onChange(e) {
  const checked = e.target.checked
  if (Array.isArray(props.modelValue)) {
    const next = [...props.modelValue]
    if (checked) {
      if (!next.includes(props.value)) next.push(props.value)
    } else {
      const idx = next.indexOf(props.value)
      if (idx >= 0) next.splice(idx, 1)
    }
    emit('update:modelValue', next)
    return
  }
  emit('update:modelValue', checked)
}
</script>

<template>
  <label
    class="form-check"
    :class="{
      'form-check--compact': resolvedDensity === 'compact',
      'form-check--disabled': disabled,
      'is-invalid': resolvedInvalid,
    }"
  >
    <input
      :id="resolvedId"
      type="checkbox"
      class="form-check-input"
      :checked="isChecked"
      :disabled="disabled"
      :value="value"
      :aria-invalid="resolvedInvalid ? true : undefined"
      :aria-describedby="describedby"
      :aria-label="ariaLabel || undefined"
      v-bind="{ ...attrs, class: undefined }"
      @change="onChange"
    />
    <span v-if="label" class="form-check-label">{{ label }}</span>
    <slot />
  </label>
</template>
